import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Church, Cake, PartyPopper, Heart, Sparkles, Award, 
  ChevronRight, ChevronLeft, Check, Calendar as CalendarIcon,
  Camera, Video, BookImage, Printer, Home, Baby, Cross, BookOpen,
  Clock, MapPin, Users, Info, Phone
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useAuth } from '../context/AuthContext';
import { 
  EVENT_PRICING as STATIC_EVENT_PRICING, 
  PACKAGE_DETAILS, 
  ADDITIONAL_SERVICES_PHOTO as STATIC_SERVICES_PHOTO,
  ADDITIONAL_SERVICES_VIDEO as STATIC_SERVICES_VIDEO,
  REFERRAL_OPTIONS,
  COVERAGE_OPTIONS,
  MINIMUM_DISCOUNT_ORDER,
} from '../utils/pricing';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventIcons = {
  portrait: Users,
  maternity: Baby,
  baptism: Church,
  communion: Cross,
  confirmation: BookOpen,
  first_birthday: Cake,
  '21st_birthday': PartyPopper,
  '50th_birthday': Award,
  civil_wedding: Heart,
  silver_golden_wedding: Sparkles,
  house_warming: Home,
  other: Camera
};

const serviceIcons = {
  on_site_prints: Printer,
  outdoor_day: Camera,
  reel_video: Video,
  drone_shots: Camera,
  full_video_hd: Video,
  full_video_4k: Video,
  photobook: BookImage
};

const BookingForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated } = useAuth();
  
  const [step, setStep] = useState(1);
  const [eventType, setEventType] = useState('');
  const [packageTier, setPackageTier] = useState(searchParams.get('package') || '');
  const [selectedDate, setSelectedDate] = useState(null);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [notes, setNotes] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [discountPercent, setDiscountPercent] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // New booking details
  const [eventTime, setEventTime] = useState('');
  const [location, setLocation] = useState('');
  const [venue, setVenue] = useState('');
  const [expectedGuests, setExpectedGuests] = useState('');
  const [coverageAreas, setCoverageAreas] = useState([]);
  const [referralSource, setReferralSource] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [saverEventType, setSaverEventType] = useState('');

  // Dynamic pricing from API
  const [EVENT_PRICING, setEventPricing] = useState(STATIC_EVENT_PRICING);
  const [ADDITIONAL_SERVICES_PHOTO, setServicesPhoto] = useState(STATIC_SERVICES_PHOTO);
  const [ADDITIONAL_SERVICES_VIDEO, setServicesVideo] = useState(STATIC_SERVICES_VIDEO);

  // Fetch pricing from API
  useEffect(() => {
    axios.get(`${API}/packages/photo`).then(res => {
      // Transform API events into EVENT_PRICING format
      const transformed = {};
      for (const event of (res.data.events || [])) {
        const eventObj = { name: event.name, coverage: event.coverage, recommended: event.recommended || '', packages: {} };
        for (const [tier, tierData] of Object.entries(event.tiers || {})) {
          eventObj[tier] = tierData.price;
          eventObj.packages[tier] = { features: tierData.features, description: tierData.description };
        }
        transformed[event.event_key] = eventObj;
      }
      if (Object.keys(transformed).length > 0) setEventPricing(transformed);
      if (res.data.services_photo?.length > 0) setServicesPhoto(res.data.services_photo.map(s => ({ id: s.service_key || s.id, name: s.name, description: s.description, price: s.price })));
      if (res.data.services_video?.length > 0) setServicesVideo(res.data.services_video.map(s => ({ id: s.service_key || s.id, name: s.name, description: s.description, price: s.price })));
    }).catch(() => {});
  }, []);

  const ADDITIONAL_SERVICES = [...ADDITIONAL_SERVICES_PHOTO, ...ADDITIONAL_SERVICES_VIDEO];

  const calculateTotal = (evtType, pkgTier, addServices = [], discPct = 0) => {
    const eventPricing = EVENT_PRICING[evtType];
    if (!eventPricing) return { basePrice: 0, servicesTotal: 0, subtotal: 0, discount: 0, total: 0 };
    const basePrice = eventPricing[pkgTier] || 0;
    const servicesTotal = addServices.reduce((sum, serviceId) => {
      const service = ADDITIONAL_SERVICES.find(s => s.id === serviceId);
      return sum + (service?.price || 0);
    }, 0);
    const subtotal = basePrice + servicesTotal;
    let discount = 0;
    if (subtotal >= MINIMUM_DISCOUNT_ORDER && discPct > 0) discount = (subtotal * discPct) / 100;
    return { basePrice, servicesTotal, subtotal, discount, total: subtotal - discount, discountApplied: subtotal >= MINIMUM_DISCOUNT_ORDER };
  };

  const getAvailablePackages = (evtType) => {
    const event = EVENT_PRICING[evtType];
    if (!event) return [];
    const available = [];
    if (event.saver_london) available.push('saver_london');
    if (event.saver_outside_london) available.push('saver_outside_london');
    if (event.starter) available.push('starter');
    if (event.classic) available.push('classic');
    if (event.elite) available.push('elite');
    return available;
  };

  // Check for saved discount
  useEffect(() => {
    const savedDiscount = localStorage.getItem('joywin_discount');
    const savedCode = localStorage.getItem('joywin_discountCode');
    if (savedDiscount && savedCode) {
      setDiscountPercent(parseInt(savedDiscount));
      setDiscountCode(savedCode);
    }
  }, []);

  // Reset package when event type changes (if not available)
  useEffect(() => {
    if (eventType) {
      const available = getAvailablePackages(eventType);
      if (packageTier && !available.includes(packageTier)) {
        setPackageTier('');
      }
    }
  }, [eventType, packageTier]);

  const pricing = calculateTotal(eventType, packageTier, additionalServices, discountPercent);
  const availablePackages = eventType ? getAvailablePackages(eventType) : [];
  const selectedEvent = EVENT_PRICING[eventType];

  const toggleService = (serviceId) => {
    setAdditionalServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const toggleCoverage = (area) => {
    setCoverageAreas(prev => 
      prev.includes(area) 
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const canProceed = () => {
    switch(step) {
      case 1: return eventType !== '';
      case 2: return packageTier !== '';
      case 3: return selectedDate !== null && eventTime !== '' && phoneNumber.trim() !== '';
      case 4: return isAuthenticated;
      default: return true;
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to complete your booking');
      return;
    }

    setIsSubmitting(true);
    try {
      const bookingData = {
        event_type: eventType,
        package_tier: packageTier,
        event_date: selectedDate.toISOString(),
        event_time: eventTime,
        location,
        venue,
        expected_guests: expectedGuests,
        coverage_areas: coverageAreas,
        referral_source: referralSource,
        phone_number: phoneNumber,
        saver_event_type: saverEventType || null,
        additional_services: additionalServices,
        notes,
        discount_code: pricing.discountApplied ? discountCode : null,
        discount_percent: pricing.discountApplied ? discountPercent : 0,
        total_price: pricing.total
      };

      await axios.post(`${API}/bookings`, bookingData);
      toast.success('Booking submitted successfully!');
      
      // Clear saved discount after booking - spin resets for new reward
      localStorage.removeItem('joywin_discount');
      localStorage.removeItem('joywin_discountCode');
      localStorage.removeItem('joywin_hasSpun');
      localStorage.removeItem('joywin_fp');
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit booking');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepVariants = {
    enter: { opacity: 0, x: 20 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="booking-form">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#EDEDED] mb-4">
            Book Your <span className="gold-gradient-text">Session</span>
          </h1>
          <p className="text-[#A1A1AA]">Complete the form below to secure your date</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-12" data-testid="booking-progress">
          {[1, 2, 3, 4].map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                step >= s 
                  ? 'gold-gradient-btn' 
                  : 'bg-[#1a1a1a] text-[#52525B]'
              }`}>
                {step > s ? <Check className="w-5 h-5" /> : s}
              </div>
              {i < 3 && (
                <div className={`w-16 h-0.5 mx-2 ${step > s ? 'bg-[#D4AF37]' : 'bg-[#1a1a1a]'}`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Steps */}
        <div className="bg-[#0a0a0a] rounded-2xl p-8 border border-white/10">
          <AnimatePresence mode="wait">
            {/* Step 1: Event Type */}
            {step === 1 && (
              <motion.div
                key="step1"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                data-testid="step-1"
              >
                <h2 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] mb-6">
                  Select Your Event Type
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {Object.entries(EVENT_PRICING).map(([key, event]) => {
                    const Icon = eventIcons[key] || Camera;
                    const startPrice = event.starter || event.classic || event.elite;
                    return (
                      <button
                        key={key}
                        onClick={() => setEventType(key)}
                        className={`p-4 rounded-xl border transition-all ${
                          eventType === key
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-white/10 hover:border-white/30'
                        }`}
                        data-testid={`event-${key}`}
                      >
                        <Icon className={`w-7 h-7 mx-auto mb-2 ${
                          eventType === key ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'
                        }`} />
                        <p className={`text-sm font-medium ${
                          eventType === key ? 'text-[#EDEDED]' : 'text-[#A1A1AA]'
                        }`}>
                          {event.name}
                        </p>
                        <p className="text-xs text-[#52525B] mt-1">
                          from £{startPrice}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {/* Step 2: Package Selection */}
            {step === 2 && (
              <motion.div
                key="step2"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                data-testid="step-2"
              >
                <h2 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] mb-2">
                  Choose Your Package
                </h2>
                {selectedEvent?.coverage && (
                  <p className="text-[#D4AF37] text-sm mb-6 flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Coverage includes: {selectedEvent.coverage}
                  </p>
                )}
                
                <div className="grid md:grid-cols-3 gap-4 mb-8">
                  {availablePackages.map((tier) => {
                    const pkg = PACKAGE_DETAILS[tier];
                    if (!pkg) return null;
                    const price = selectedEvent?.[tier];
                    const eventPkg = selectedEvent?.packages?.[tier];
                    const isRecommended = selectedEvent?.recommended === tier;
                    
                    return (
                      <button
                        key={tier}
                        onClick={() => setPackageTier(tier)}
                        className={`p-6 rounded-xl border text-left transition-all relative ${
                          packageTier === tier
                            ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                            : 'border-white/10 hover:border-white/30'
                        } ${pkg.highlight ? 'ring-1 ring-[#D4AF37]/30' : ''}`}
                        data-testid={`package-select-${tier}`}
                      >
                        {isRecommended && (
                          <span className="absolute -top-3 left-4 text-xs bg-[#D4AF37] text-black px-2 py-0.5 rounded-full">
                            Recommended
                          </span>
                        )}
                        {pkg.highlight && !isRecommended && (
                          <span className="text-xs text-[#D4AF37] mb-2 block">Popular</span>
                        )}
                        <h3 className="text-lg font-semibold text-[#EDEDED] mb-1">{pkg.name}</h3>
                        <p className="text-3xl font-['Playfair_Display'] text-[#D4AF37] mb-3">
                          £{price}
                        </p>
                        <p className="text-xs text-[#52525B] mb-3">
                          Delivery: {pkg.deliveryTime}
                        </p>
                        <ul className="space-y-2">
                          {(eventPkg?.features || pkg.features).slice(0, 5).map((f, i) => (
                            <li key={i} className="text-xs text-[#A1A1AA] flex items-start gap-2">
                              <Check className="w-3 h-3 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                              <span>{f}</span>
                            </li>
                          ))}
                        </ul>
                        {(eventPkg?.note || pkg.note) && (
                          <p className="text-[10px] text-[#52525B] mt-3 italic">{eventPkg?.note || pkg.note}</p>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Additional Services - Photography */}
                <div className="mb-6">
                  <h3 className="font-['Playfair_Display'] text-xl text-[#EDEDED] mb-4 flex items-center gap-2">
                    <Camera className="w-5 h-5 text-[#D4AF37]" />
                    Additional Photography Services
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {ADDITIONAL_SERVICES_PHOTO.map((service) => {
                      const Icon = serviceIcons[service.id] || Camera;
                      return (
                        <button
                          key={service.id}
                          onClick={() => toggleService(service.id)}
                          className={`p-4 rounded-xl border flex items-start gap-4 text-left transition-all ${
                            additionalServices.includes(service.id)
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                          data-testid={`service-${service.id}`}
                        >
                          <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            additionalServices.includes(service.id) ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'
                          }`} />
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <h4 className="text-sm font-medium text-[#EDEDED]">{service.name}</h4>
                              <span className="text-[#D4AF37] font-semibold">+£{service.price}</span>
                            </div>
                            <p className="text-xs text-[#52525B] mt-1">{service.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Additional Services - Videography */}
                <div>
                  <h3 className="font-['Playfair_Display'] text-xl text-[#EDEDED] mb-4 flex items-center gap-2">
                    <Video className="w-5 h-5 text-[#D4AF37]" />
                    Videography Services
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    {ADDITIONAL_SERVICES_VIDEO.map((service) => {
                      const Icon = serviceIcons[service.id] || Video;
                      return (
                        <button
                          key={service.id}
                          onClick={() => toggleService(service.id)}
                          className={`p-4 rounded-xl border flex items-start gap-4 text-left transition-all ${
                            additionalServices.includes(service.id)
                              ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                              : 'border-white/10 hover:border-white/30'
                          }`}
                          data-testid={`service-${service.id}`}
                        >
                          <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                            additionalServices.includes(service.id) ? 'text-[#D4AF37]' : 'text-[#A1A1AA]'
                          }`} />
                          <div className="flex-1">
                            <div className="flex justify-between items-start">
                              <h4 className="text-sm font-medium text-[#EDEDED]">{service.name}</h4>
                              <span className="text-[#D4AF37] font-semibold">+£{service.price}</span>
                            </div>
                            <p className="text-xs text-[#52525B] mt-1">{service.description}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 3: Date & Details */}
            {step === 3 && (
              <motion.div
                key="step3"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                data-testid="step-3"
              >
                <h2 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] mb-6">
                  Event Details
                </h2>
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Left Column - Calendar */}
                  <div>
                    <label className="text-sm text-[#A1A1AA] block mb-2">Select Date *</label>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date()}
                      className="rounded-xl border border-white/10 bg-[#121212] p-4"
                      data-testid="date-calendar"
                    />
                  </div>

                  {/* Right Column - Details */}
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2">
                        <Clock className="w-4 h-4" /> Event Time *
                      </label>
                      <Input
                        type="time"
                        value={eventTime}
                        onChange={(e) => setEventTime(e.target.value)}
                        className="bg-[#121212] border-white/10 text-[#EDEDED]"
                        data-testid="event-time"
                        required
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2">
                        <Phone className="w-4 h-4" /> Phone Number *
                      </label>
                      <Input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="e.g. +44 7123 456789"
                        className="bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                        data-testid="phone-number"
                        required
                      />
                      <p className="text-xs text-[#D4AF37] mt-1.5">We will be contacting you shortly.</p>
                    </div>

                    <div>
                      <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Location
                      </label>
                      <Input
                        type="text"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        placeholder="City/Area"
                        className="bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                        data-testid="location"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#A1A1AA] block mb-2">Venue Name</label>
                      <Input
                        type="text"
                        value={venue}
                        onChange={(e) => setVenue(e.target.value)}
                        placeholder="e.g. St. Mary's Church"
                        className="bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                        data-testid="venue"
                      />
                    </div>

                    {(packageTier === 'saver_london' || packageTier === 'saver_outside_london') && (
                      <div>
                        <label className="text-sm text-[#A1A1AA] block mb-2">Event Type *</label>
                        <select
                          value={saverEventType}
                          onChange={(e) => setSaverEventType(e.target.value)}
                          className="w-full bg-[#121212] border border-white/10 rounded-md px-3 py-2 text-[#EDEDED] text-sm"
                          data-testid="saver-event-type"
                        >
                          <option value="">Select event type</option>
                          <option value="small_family_gatherings">Small family gatherings</option>
                          <option value="baptism">Baptism</option>
                          <option value="engagement_parties">Engagement parties</option>
                          <option value="community_events">Community events</option>
                          <option value="religious_gatherings">Religious gatherings (small functions)</option>
                        </select>
                      </div>
                    )}

                    <div>
                      <label className="text-sm text-[#A1A1AA] block mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4" /> Expected Number of Guests
                      </label>
                      <Input
                        type="number"
                        value={expectedGuests}
                        onChange={(e) => setExpectedGuests(e.target.value)}
                        placeholder="Enter number"
                        className="bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                        data-testid="expected-guests"
                      />
                    </div>

                    <div>
                      <label className="text-sm text-[#A1A1AA] block mb-2">Where do you want us to cover?</label>
                      <div className="flex flex-wrap gap-2">
                        {COVERAGE_OPTIONS.map((area) => (
                          <button
                            key={area}
                            type="button"
                            onClick={() => toggleCoverage(area)}
                            className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                              coverageAreas.includes(area)
                                ? 'bg-[#D4AF37] text-black'
                                : 'bg-[#1a1a1a] text-[#A1A1AA] border border-white/10'
                            }`}
                            data-testid={`coverage-${area.toLowerCase().replace(' ', '-')}`}
                          >
                            {area}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div>
                      <label className="text-sm text-[#A1A1AA] block mb-2">Who recommended us?</label>
                      <Select value={referralSource} onValueChange={setReferralSource}>
                        <SelectTrigger className="bg-[#121212] border-white/10 text-[#EDEDED]" data-testid="referral-source">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-white/10">
                          {REFERRAL_OPTIONS.map((option) => (
                            <SelectItem key={option} value={option} className="text-[#EDEDED]">
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <label className="text-sm text-[#A1A1AA] block mb-2">Additional Notes</label>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any special requests or details..."
                        className="bg-[#121212] border-white/10 text-[#EDEDED] placeholder:text-[#52525B]"
                        rows={3}
                        data-testid="booking-notes"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 4: Review & Submit */}
            {step === 4 && (
              <motion.div
                key="step4"
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.3 }}
                data-testid="step-4"
              >
                <h2 className="font-['Playfair_Display'] text-2xl text-[#EDEDED] mb-6">
                  Review & Confirm
                </h2>

                {!isAuthenticated ? (
                  <div className="text-center py-8 bg-[#121212] rounded-xl border border-white/10 mb-6">
                    <p className="text-[#A1A1AA] mb-4">Please login or create an account to complete your booking</p>
                    <div className="flex gap-4 justify-center">
                      <Link to="/login?redirect=/book">
                        <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37]" data-testid="booking-login-btn">
                          Login
                        </Button>
                      </Link>
                      <Link to="/register?redirect=/book">
                        <Button className="gold-gradient-btn" data-testid="booking-register-btn">
                          Create Account
                        </Button>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#121212] rounded-xl p-6 border border-white/10 mb-6">
                    <p className="text-[#A1A1AA]">Booking as:</p>
                    <p className="text-[#EDEDED] font-medium">{user?.name} ({user?.email})</p>
                  </div>
                )}

                {/* Order Summary */}
                <div className="bg-[#121212] rounded-xl p-6 border border-white/10">
                  <h3 className="text-lg font-semibold text-[#EDEDED] mb-4">Order Summary</h3>
                  
                  {/* Event Details */}
                  <div className="space-y-2 text-sm border-b border-white/10 pb-4 mb-4">
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Event:</span>
                      <span className="text-[#EDEDED]">{EVENT_PRICING[eventType]?.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Package:</span>
                      <span className="text-[#EDEDED]">{PACKAGE_DETAILS[packageTier]?.name}</span>
                    </div>
                    {selectedDate && (
                      <div className="flex justify-between">
                        <span className="text-[#A1A1AA]">Date:</span>
                        <span className="text-[#EDEDED]">
                          {selectedDate.toLocaleDateString('en-GB', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                    )}
                    {eventTime && (
                      <div className="flex justify-between">
                        <span className="text-[#A1A1AA]">Time:</span>
                        <span className="text-[#EDEDED]">{eventTime}</span>
                      </div>
                    )}
                  </div>

                  {/* Pricing */}
                  <div className="space-y-2 text-sm border-b border-white/10 pb-4 mb-4">
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">{PACKAGE_DETAILS[packageTier]?.name} Package</span>
                      <span className="text-[#EDEDED]">£{pricing.basePrice}</span>
                    </div>
                    {additionalServices.map(serviceId => {
                      const service = [...ADDITIONAL_SERVICES_PHOTO, ...ADDITIONAL_SERVICES_VIDEO].find(s => s.id === serviceId);
                      return (
                        <div key={serviceId} className="flex justify-between">
                          <span className="text-[#A1A1AA]">{service?.name}</span>
                          <span className="text-[#EDEDED]">£{service?.price}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Discount */}
                  {discountPercent > 0 && (
                    <div className="mb-4">
                      {pricing.discountApplied ? (
                        <div className="flex justify-between text-sm text-green-500">
                          <span>Discount ({discountPercent}%)</span>
                          <span>-£{pricing.discount.toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 text-sm text-yellow-500">
                          <Info className="w-4 h-4" />
                          <span>Discount applies on orders over £{MINIMUM_DISCOUNT_ORDER}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex justify-between text-lg font-semibold">
                    <span className="text-[#EDEDED]">Total</span>
                    <span className="text-[#D4AF37]">£{pricing.total.toFixed(2)}</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
            <Button
              variant="outline"
              onClick={() => setStep(s => Math.max(1, s - 1))}
              disabled={step === 1}
              className="border-white/20 text-[#A1A1AA] hover:text-[#EDEDED]"
              data-testid="prev-step-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>

            {step < 4 ? (
              <Button
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
                className="gold-gradient-btn"
                data-testid="next-step-btn"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={!isAuthenticated || isSubmitting}
                className="gold-gradient-btn"
                data-testid="submit-booking-btn"
              >
                {isSubmitting ? 'Submitting...' : 'Confirm Booking'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
